import { useQuery } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import { Card, EmptyState, LoadingBlock } from '@/design'
import { ga4Keys } from '@/features/ga4-shared/keys'

import '../integrations.css'
import './ga4.css'
import { ConnectForm } from './ConnectForm'
import { Ga4Error } from './errors'
import { PropertiesCard } from './PropertiesCard'
import { RunAllButton } from './RunAllButton'
import { ServiceAccountCard } from './ServiceAccountCard'

/**
 * Integrations › Google Analytics: grant TmT's service account Viewer on a GA4
 * property, connect it, and see when its traffic was last imported. The Traffic
 * page under Analytics › Website reads what this page connects.
 */
export function Ga4IntegrationPage() {
  const client = useApiClient()
  const state = useQuery({ queryKey: ga4Keys.integration(client.kind), queryFn: () => client.ga4Integration() })

  if (state.isPending) return <LoadingBlock rows={4} label="Loading the Google Analytics connection" />
  if (state.isError) {
    return <Ga4Error error={state.error} email={null} fallback="The Google Analytics connection could not be loaded." />
  }

  const { configured, serviceAccountEmail, configurationError, properties } = state.data
  if (!configured || !serviceAccountEmail) {
    return (
      <Card title="Google Analytics 4">
        <EmptyState
          variant="disconnected"
          title="Not configured on this environment"
          description="Google Analytics cannot be connected here yet: TmT support has to finish setting it up. Nothing on your side needs to change."
        />
        {configurationError ? <p className="ci-integration__note">For TmT support — {configurationError}</p> : null}
      </Card>
    )
  }

  return (
    <div className="ci-integration">
      <ServiceAccountCard email={serviceAccountEmail} />
      <ConnectForm email={serviceAccountEmail} />
      <RunAllButton properties={properties} email={serviceAccountEmail} />
      <PropertiesCard properties={properties} email={serviceAccountEmail} />
    </div>
  )
}
